import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import inventoryService from '../services/inventoryService';
import productOptionsService from '../services/productOptionsService';
import toast from 'react-hot-toast';
import ConfirmModal from './ConfirmModal';
import {
  Plus, Trash2, X, Edit2, Package, Search, CheckSquare, Square,
  Power, PowerOff, Image, Upload, ChevronDown, ChevronRight,
  Layers, DollarSign
} from 'lucide-react';

const ProductsManager = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);
  const [units, setUnits] = useState([]);
  const [search, setSearch] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [showInactive, setShowInactive] = useState(false);

  // Estado para expandir produtos e ver variantes
  const [expandedProducts, setExpandedProducts] = useState([]);
  const [productVariants, setProductVariants] = useState({});
  const [loadingVariants, setLoadingVariants] = useState({});

  // Estado para variantes no formulário
  const [variants, setVariants] = useState([]);
  const [editingVariant, setEditingVariant] = useState(null);
  const [showVariantForm, setShowVariantForm] = useState(false);

  // Opções disponíveis de ProductOptionsManager
  const [availableOptions, setAvailableOptions] = useState({
    marcas: [],
    cores: [],
    tamanhos: []
  });

  // Form state
  const [formData, setFormData] = useState({
    nome: '',
    sku: '',
    codigo_interno: '',
    descricao: '',
    categoria_id: '',
    marca_id: '',
    unidade_id: '',
    ativo: true,
    imagem_url: ''
  });
  const [imagemPreview, setImagemPreview] = useState(null);
  const fileInputRef = useRef(null);

  // Confirm modal states
  const [confirmModal, setConfirmModal] = useState({
    show: false,
    title: 'Confirmar',
    message: '',
    type: 'warning',
    onConfirm: null
  });

  // Estado do formulário de variante
  const [variantForm, setVariantForm] = useState({
    sku_variacao: '',
    cor: '',
    tamanho_label: '',
    medida_text: '',
    espessura_mm: '',
    comprimento_cm: '',
    largura_cm: '',
    preco_custo: '',
    preco_venda: '',
    ativo: true
  });

  useEffect(() => {
    loadProducts();
    loadCategories();
    loadBrands();
    loadUnits();
    loadAvailableOptions();
  }, []);

  // Carregar opções disponíveis das tabelas de dados auxiliar
  const loadAvailableOptions = async () => {
    try {
      // Buscar das tabelas de dados auxiliares
      const [coresResponse, tamanhosResponse] = await Promise.all([
        inventoryService.getCores(),
        inventoryService.getTamanhos()
      ]);

      const cores = coresResponse.success ? coresResponse.data.filter(c => c.ativo).map(c => c.nome) : [];
      const tamanhos = tamanhosResponse.success ? tamanhosResponse.data.filter(t => t.ativo).map(t => t.nome) : [];

      // Também buscar do ProductOptionsManager para compatibilidade
      let marcasFromOptions = [];
      try {
        const response = await productOptionsService.getAllAvailable();
        if (response.success && response.data) {
          response.data.forEach(option => {
            if (option.atributos && option.atributos.marcas) {
              option.atributos.marcas.forEach(m => marcasFromOptions.push(m));
            }
          });
        }
      } catch (e) {
        console.warn('Erro ao buscar marcas do ProductOptionsService:', e);
      }

      setAvailableOptions({
        marcas: [...new Set(marcasFromOptions)].sort(),
        cores: cores.sort(),
        tamanhos: tamanhos.sort()
      });
    } catch (error) {
      console.error('Erro ao carregar opções disponíveis:', error);
    }
  };

  const loadProducts = async () => {
    setLoading(true);
    try {
      const params = { search };
      if (showInactive) {
        params.ativo = 'all';
      }
      const response = await inventoryService.getProducts(params);
      if (response.success) {
        setProducts(response.data);
      }
    } catch (error) {
      console.error('Erro ao carregar produtos:', error);
      toast.error('Erro ao carregar produtos');
    } finally {
      setLoading(false);
    }
  };

  const loadCategories = async () => {
    try {
      const response = await inventoryService.getCategories();
      if (response.success) {
        setCategories(response.data.filter(c => c.ativo));
      }
    } catch (error) {
      console.error('Erro ao carregar categorias:', error);
    }
  };

  const loadBrands = async () => {
    try {
      const response = await inventoryService.getBrands();
      if (response.success) {
        setBrands(response.data.filter(b => b.ativo));
      }
    } catch (error) {
      console.error('Erro ao carregar marcas:', error);
    }
  };

  const loadUnits = async () => {
    try {
      const response = await inventoryService.getUnits();
      if (response.success) {
        setUnits(response.data);
      }
    } catch (error) {
      console.error('Erro ao carregar unidades:', error);
    }
  };

  // Carregar variantes de um produto
  const loadProductVariants = async (productId) => {
    if (productVariants[productId]) return; // Já carregado

    setLoadingVariants(prev => ({ ...prev, [productId]: true }));
    try {
      const response = await inventoryService.getProduct(productId);
      if (response.success && response.data.variantes) {
        setProductVariants(prev => ({
          ...prev,
          [productId]: response.data.variantes
        }));
      }
    } catch (error) {
      console.error('Erro ao carregar variantes:', error);
    } finally {
      setLoadingVariants(prev => ({ ...prev, [productId]: false }));
    }
  };

  // Alternar expansão do produto
  const toggleProductExpand = (productId) => {
    if (expandedProducts.includes(productId)) {
      setExpandedProducts(prev => prev.filter(id => id !== productId));
    } else {
      setExpandedProducts(prev => [...prev, productId]);
      loadProductVariants(productId);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    loadProducts();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.nome) {
      toast.error('Nome do produto é obrigatório');
      return;
    }

    try {
      let productId;

      if (editingProduct) {
        await inventoryService.updateProduct(editingProduct.id, formData);
        productId = editingProduct.id;
        toast.success('Produto atualizado com sucesso!');
      } else {
        const response = await inventoryService.createProduct(formData);
        productId = response.data.id;
        toast.success('Produto criado com sucesso!');
      }

      // Salvar variantes
      await saveVariants(productId);

      resetForm();
      loadProducts();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Erro ao salvar produto');
    }
  };

  // Salvar variantes do produto
  const saveVariants = async (productId) => {
    for (const variant of variants) {
      try {
        if (variant.id && typeof variant.id === 'number' && variant.id > 0 && !variant.temp) {
          // Variante existente no banco (ID positivo) - atualizar
          await inventoryService.updateVariant(variant.id, {
            sku_variacao: variant.sku_variacao,
            cor: variant.cor || null,
            tamanho_label: variant.tamanho_label || null,
            medida_text: variant.medida_text || null,
            espessura_mm: variant.espessura_mm || null,
            comprimento_cm: variant.comprimento_cm || null,
            largura_cm: variant.largura_cm || null,
            preco_custo: variant.preco_custo || 0,
            preco_venda: variant.preco_venda || 0,
            ativo: variant.ativo !== false
          });
        } else {
          // Nova variante - criar
          await inventoryService.createVariant(productId, {
            sku_variacao: variant.sku_variacao,
            cor: variant.cor || null,
            tamanho_label: variant.tamanho_label || null,
            medida_text: variant.medida_text || null,
            espessura_mm: variant.espessura_mm || null,
            comprimento_cm: variant.comprimento_cm || null,
            largura_cm: variant.largura_cm || null,
            preco_custo: variant.preco_custo || 0,
            preco_venda: variant.preco_venda || 0,
            ativo: variant.ativo !== false
          });
        }
      } catch (error) {
        console.error('Erro ao salvar variante:', error);
        toast.error(`Erro ao salvar variante: ${variant.sku_variacao || 'sem SKU'}`);
      }
    }
  };

  const handleEdit = async (product) => {
    setEditingProduct(product);
    setFormData({
      nome: product.nome || '',
      sku: product.sku || '',
      codigo_interno: product.codigo_interno || '',
      descricao: product.descricao || '',
      categoria_id: product.categoria_id || '',
      marca_id: product.marca_id || '',
      unidade_id: product.unidade_id || '',
      ativo: product.ativo,
      imagem_url: product.imagem_url || ''
    });
    setImagemPreview(product.imagem_url || null);

    // Carregar variantes do produto
    try {
      const response = await inventoryService.getProduct(product.id);
      if (response.success && response.data.variantes) {
        setVariants(response.data.variantes);
        setProductVariants(prev => ({
          ...prev,
          [product.id]: response.data.variantes
        }));
      }
    } catch (error) {
      console.error('Erro ao carregar variantes:', error);
      setVariants([]);
    }

    setShowForm(true);
  };

  // Função para lidar com upload de imagem
  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.match(/image\/(jpeg|jpg|png|gif|webp)/)) {
        toast.error('Apenas arquivos de imagem (JPEG, PNG, GIF, WebP) são permitidos');
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        toast.error('A imagem deve ter no máximo 5MB');
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData({ ...formData, imagem_url: reader.result });
        setImagemPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  // Remover imagem
  const removeImage = () => {
    setFormData({ ...formData, imagem_url: '' });
    setImagemPreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // ========== FUNÇÕES DE VARIANTES ==========

  // Adicionar nova variante
  const addVariant = () => {
    setEditingVariant(null);
    setVariantForm({
      sku_variacao: '',
      cor: '',
      tamanho_label: '',
      medida_text: '',
      espessura_mm: '',
      comprimento_cm: '',
      largura_cm: '',
      preco_custo: '',
      preco_venda: '',
      ativo: true
    });
    setShowVariantForm(true);
  };

  // Editar variante existente
  const editVariant = (variant) => {
    setEditingVariant(variant);
    setVariantForm({
      sku_variacao: variant.sku_variacao || '',
      cor: variant.cor || '',
      tamanho_label: variant.tamanho_label || '',
      medida_text: variant.medida_text || '',
      espessura_mm: variant.espessura_mm || '',
      comprimento_cm: variant.comprimento_cm || '',
      largura_cm: variant.largura_cm || '',
      preco_custo: variant.preco_custo || '',
      preco_venda: variant.preco_venda || '',
      ativo: variant.ativo
    });
    setShowVariantForm(true);
  };

  // Salvar variante (no formulário)
  const saveVariant = () => {
    if (editingVariant) {
      // Atualizar variante existente na lista
      setVariants(prev => prev.map(v =>
        v.id === editingVariant.id ? { ...v, ...variantForm } : v
      ));
    } else {
      // Nova variante com ID temporário negativo
      const newVariant = {
        ...variantForm,
        id: -Date.now(), // ID temporário negativo
        temp: true
      };
      setVariants(prev => [...prev, newVariant]);
    }
    setShowVariantForm(false);
  };

  // Remover variante
  const removeVariant = async (variant) => {
    if (variant.id && typeof variant.id === 'number') {
      setConfirmModal({
        show: true,
        title: 'Excluir Variação',
        message: 'Tem certeza que deseja excluir esta variante?',
        type: 'danger',
        onConfirm: async () => {
          try {
            await inventoryService.deleteVariant(variant.id);
            toast.success('Variante excluída!');
            setVariants(prev => prev.filter(v => v.id !== variant.id));
            if (editingProduct) {
              setProductVariants(prev => ({
                ...prev,
                [editingProduct.id]: (prev[editingProduct.id] || []).filter(v => v.id !== variant.id)
              }));
            }
          } catch (error) {
            toast.error(error.response?.data?.message || 'Erro ao excluir variante');
          }
          setConfirmModal(prev => ({ ...prev, show: false }));
        }
      });
    } else {
      setVariants(prev => prev.filter(v => v.id !== variant.id));
    }
  };

  // ========== FIM FUNÇÕES DE VARIANTES ==========

  // Inativar produto (soft delete)
  const handleInactivate = async (id) => {
    setConfirmModal({
      show: true,
      title: 'Inativar Produto',
      message: 'Tem certeza que deseja inativar este produto?',
      type: 'warning',
      onConfirm: async () => {
        try {
          await inventoryService.updateProduct(id, { ativo: false });
          toast.success('Produto inativado com sucesso!');
          loadProducts();
        } catch (error) {
          toast.error('Erro ao inativar produto');
        }
        setConfirmModal(prev => ({ ...prev, show: false }));
      }
    });
  };

  // Ativar produto
  const handleActivate = async (id) => {
    try {
      await inventoryService.updateProduct(id, { ativo: true });
      toast.success('Produto ativado com sucesso!');
      loadProducts();
    } catch (error) {
      toast.error('Erro ao ativar produto');
    }
  };

  // Excluir definitivamente (hard delete)
  const handleDelete = async (id) => {
    setConfirmModal({
      show: true,
      title: 'Excluir Produto',
      message: 'ATENÇÃO: Exclusão definitiva! O produto será removido permanentemente. Continuar?',
      type: 'danger',
      onConfirm: async () => {
        try {
          await inventoryService.deleteProduct(id);
          toast.success('Produto excluído com sucesso!');
          loadProducts();
        } catch (error) {
          const msg = error.response?.data?.message || 'Erro ao excluir produto';
          toast.error(msg);
        }
        setConfirmModal(prev => ({ ...prev, show: false }));
      }
    });
  };

  // Seleção individual
  const toggleSelectProduct = (id) => {
    setSelectedProducts(prev =>
      prev.includes(id)
        ? prev.filter(p => p !== id)
        : [...prev, id]
    );
  };

  // Seleção todos
  const toggleSelectAll = () => {
    if (selectedProducts.length === products.length) {
      setSelectedProducts([]);
    } else {
      setSelectedProducts(products.map(p => p.id));
    }
  };

  // Inativar selecionados
  const handleInactivateSelected = async () => {
    if (selectedProducts.length === 0) {
      toast.error('Nenhum produto selecionado');
      return;
    }

    setConfirmModal({
      show: true,
      title: 'Inativar Produtos',
      message: `Tem certeza que deseja inativar ${selectedProducts.length} produto(s)?`,
      type: 'warning',
      onConfirm: async () => {
        try {
          setLoading(true);
          for (const id of selectedProducts) {
            await inventoryService.updateProduct(Number(id), { ativo: false });
          }
          toast.success(`${selectedProducts.length} produto(s) inativado(s) com sucesso!`);
          setSelectedProducts([]);
          loadProducts();
        } catch (error) {
          toast.error('Erro ao inativar produtos');
        } finally {
          setLoading(false);
        }
        setConfirmModal(prev => ({ ...prev, show: false }));
      }
    });
  };

  // Excluir selecionados definitivamente
  const handleDeleteSelected = async () => {
    if (selectedProducts.length === 0) {
      toast.error('Nenhum produto selecionado');
      return;
    }

    setConfirmModal({
      show: true,
      title: 'Excluir Produtos',
      message: `ATENÇÃO: Exclusão definitiva! ${selectedProducts.length} produto(s) será(ão) removido(s) permanentemente. Continuar?`,
      type: 'danger',
      onConfirm: async () => {
        try {
          setLoading(true);
          for (const id of selectedProducts) {
            await inventoryService.deleteProduct(Number(id));
          }
          toast.success(`${selectedProducts.length} produto(s) excluído(s) com sucesso!`);
          setSelectedProducts([]);
          loadProducts();
        } catch (error) {
          const msg = error.response?.data?.message || 'Erro ao excluir produtos';
          toast.error(msg);
        } finally {
          setLoading(false);
        }
        setConfirmModal(prev => ({ ...prev, show: false }));
      }
    });
  };

  const resetForm = () => {
    setShowForm(false);
    setEditingProduct(null);
    setFormData({
      nome: '',
      sku: '',
      codigo_interno: '',
      descricao: '',
      categoria_id: '',
      marca_id: '',
      unidade_id: '',
      ativo: true,
      imagem_url: ''
    });
    setImagemPreview(null);
    setVariants([]);
    setShowVariantForm(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Obter variantes para exibir (do formulário ou do produto carregado)
  const getDisplayVariants = (productId) => {
    if (editingProduct && editingProduct.id === productId) {
      return variants;
    }
    return productVariants[productId] || [];
  };

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Gestão de Produtos</h1>
          <p className="text-gray-600">Cadastre e gerencie os produtos com variantes</p>
        </div>
        <button
          onClick={() => setShowForm(true)}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          <Plus className="w-4 h-4" />
          Novo Produto
        </button>
      </div>

      {/* Search and Actions */}
      <div className="mb-6 flex gap-4 flex-wrap">
        <form onSubmit={handleSearch} className="flex-1 flex gap-4 min-w-[300px]">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Buscar por nome, SKU ou código..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <button type="submit" className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700">
            Buscar
          </button>
        </form>

        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={showInactive}
            onChange={(e) => {
              setShowInactive(e.target.checked);
              loadProducts();
            }}
            className="w-4 h-4 text-blue-600 border-gray-300 rounded"
          />
          <span className="text-sm text-gray-700">Mostrar inativos</span>
        </label>

        {selectedProducts.length > 0 && (
          <div className="flex gap-2">
            <button
              onClick={handleInactivateSelected}
              className="flex items-center gap-2 px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600"
              title="Inativar selecionados"
            >
              <PowerOff className="w-4 h-4" />
              Inativar ({selectedProducts.length})
            </button>
            <button
              onClick={handleDeleteSelected}
              className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
              title="Excluir definitivamente"
            >
              <Trash2 className="w-4 h-4" />
              Excluir ({selectedProducts.length})
            </button>
          </div>
        )}
      </div>

      {/* Products List */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left w-8">
                <button
                  onClick={toggleSelectAll}
                  className="text-gray-500 hover:text-gray-700"
                  title={selectedProducts.length === products.length ? "Desmarcar todos" : "Selecionar todos"}
                >
                  {selectedProducts.length === products.length && products.length > 0 ? (
                    <CheckSquare className="w-5 h-5" />
                  ) : (
                    <Square className="w-5 h-5" />
                  )}
                </button>
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-16">
                Imagem
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Produto
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                SKU
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Categoria
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Marca
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Variantes
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Ações
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {loading ? (
              <tr>
                <td colSpan="9" className="px-6 py-4 text-center text-gray-500">
                  Carregando...
                </td>
              </tr>
            ) : products.length === 0 ? (
              <tr>
                <td colSpan="9" className="px-6 py-4 text-center text-gray-500">
                  Nenhum produto encontrado
                </td>
              </tr>
            ) : (
              products.map((product) => (
                <React.Fragment key={product.id}>
                  <tr className={`hover:bg-gray-50 ${selectedProducts.includes(product.id) ? 'bg-blue-50' : ''} ${!product.ativo ? 'bg-gray-100' : ''}`}>
                    <td className="px-4 py-4">
                      <button
                        onClick={() => toggleSelectProduct(product.id)}
                        className="text-gray-500 hover:text-blue-600"
                      >
                        {selectedProducts.includes(product.id) ? (
                          <CheckSquare className="w-5 h-5 text-blue-600" />
                        ) : (
                          <Square className="w-5 h-5" />
                        )}
                      </button>
                    </td>
                    <td className="px-4 py-4">
                      {product.imagem_url ? (
                        <img
                          src={product.imagem_url}
                          alt={product.nome}
                          className="w-12 h-12 object-cover rounded-lg border border-gray-200"
                        />
                      ) : (
                        <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center">
                          <Image className="w-6 h-6 text-gray-300" />
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center">
                        {(product.total_variantes > 0 || getDisplayVariants(product.id).length > 0) && (
                          <button
                            onClick={() => toggleProductExpand(product.id)}
                            className="mr-2 text-gray-400 hover:text-gray-600"
                          >
                            {expandedProducts.includes(product.id) ? (
                              <ChevronDown className="w-5 h-5" />
                            ) : (
                              <ChevronRight className="w-5 h-5" />
                            )}
                          </button>
                        )}
                        <Package className="w-5 h-5 text-gray-400 mr-3" />
                        <div>
                          <div className="text-sm font-medium text-gray-900">{product.nome}</div>
                          {product.descricao && (
                            <div className="text-sm text-gray-500">{product.descricao.substring(0, 40)}...</div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {product.sku || '-'}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {product.categoria_nome || '-'}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {product.marca_nome || '-'}
                    </td>
                    <td className="px-6 py-4">
                      <span className="inline-flex items-center gap-1 px-2 py-1 bg-purple-100 text-purple-800 text-xs rounded-full">
                        <Layers className="w-3 h-3" />
                        {getDisplayVariants(product.id).length || product.total_variantes || 0}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 text-xs rounded-full ${
                        product.ativo
                          ? 'bg-green-100 text-green-800'
                          : 'bg-gray-200 text-gray-600'
                      }`}>
                        {product.ativo ? 'Ativo' : 'Inativo'}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex justify-end gap-2">
                        {product.ativo ? (
                          <button
                            onClick={() => handleInactivate(product.id)}
                            className="text-orange-600 hover:text-orange-800 p-1"
                            title="Inativar"
                          >
                            <PowerOff className="w-4 h-4" />
                          </button>
                        ) : (
                          <button
                            onClick={() => handleActivate(product.id)}
                            className="text-green-600 hover:text-green-800 p-1"
                            title="Ativar"
                          >
                            <Power className="w-4 h-4" />
                          </button>
                        )}
                        <button
                          onClick={() => handleEdit(product)}
                          className="text-blue-600 hover:text-blue-800 p-1"
                          title="Editar"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(product.id)}
                          className="text-red-600 hover:text-red-800 p-1"
                          title="Excluir definitivamente"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                  {/* Linha de variantes expandida */}
                  {expandedProducts.includes(product.id) && (
                    <tr className="bg-gray-50">
                      <td colSpan="9" className="px-6 py-4">
                        <div className="ml-8">
                          <div className="flex items-center justify-between mb-2">
                            <h4 className="text-sm font-medium text-gray-700">Variações do produto</h4>
                          </div>
                          {loadingVariants[product.id] ? (
                            <p className="text-sm text-gray-500">Carregando variantes...</p>
                          ) : getDisplayVariants(product.id).length === 0 ? (
                            <p className="text-sm text-gray-500">Nenhuma variante cadastrada</p>
                          ) : (
                            <div className="bg-white rounded border border-gray-200 overflow-hidden">
                              <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-100">
                                  <tr>
                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">SKU</th>
                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Cor</th>
                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Tamanho</th>
                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Custo</th>
                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Venda</th>
                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">R$/m²</th>
                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Estoque</th>
                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Status</th>
                                    <th className="px-4 py-2 text-right text-xs font-medium text-gray-500">Ações</th>
                                  </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-200">
                                  {getDisplayVariants(product.id).map((variant) => (
                                    <tr key={variant.id} className="hover:bg-gray-50">
                                      <td className="px-4 py-2 text-sm text-gray-900">{variant.sku_variacao || '-'}</td>
                                      <td className="px-4 py-2 text-sm text-gray-500">{variant.cor || '-'}</td>
                                      <td className="px-4 py-2 text-sm text-gray-500">{variant.tamanho_label || variant.medida_text || '-'}</td>
                                      <td className="px-4 py-2 text-sm text-gray-500">
                                        {variant.preco_custo ? `R$ ${parseFloat(variant.preco_custo).toFixed(2)}` : '-'}
                                      </td>
                                      <td className="px-4 py-2 text-sm text-gray-500">
                                        {variant.preco_venda ? `R$ ${parseFloat(variant.preco_venda).toFixed(2)}` : '-'}
                                      </td>
                                      <td className="px-4 py-2 text-sm">
                                        {variant.comprimento_cm > 0 && variant.largura_cm > 0 && variant.preco_venda > 0 ? (
                                          <span className="px-2 py-1 rounded-full text-xs bg-green-100 text-green-800">
                                            R$ {((variant.preco_venda / ((variant.comprimento_cm * variant.largura_cm) / 10000))).toFixed(2)}/m²
                                          </span>
                                        ) : (
                                          <span className="text-gray-400">-</span>
                                        )}
                                      </td>
                                      <td className="px-4 py-2 text-sm">
                                        <span className={`px-2 py-1 rounded-full text-xs ${
                                          (variant.estoque_disponivel || 0) > 0
                                            ? 'bg-green-100 text-green-800'
                                            : 'bg-red-100 text-red-800'
                                        }`}>
                                          {variant.estoque_disponivel || 0}
                                        </span>
                                      </td>
                                      <td className="px-4 py-2">
                                        <span className={`px-2 py-1 text-xs rounded-full ${
                                          variant.ativo ? 'bg-green-100 text-green-800' : 'bg-gray-200 text-gray-600'
                                        }`}>
                                          {variant.ativo ? 'Ativo' : 'Inativo'}
                                        </span>
                                      </td>
                                      <td className="px-4 py-2 text-right">
                                        {editingProduct && editingProduct.id === product.id && (
                                          <div className="flex justify-end gap-1">
                                            <button
                                              onClick={() => editVariant(variant)}
                                              className="text-blue-600 hover:text-blue-800 p-1"
                                              title="Editar"
                                            >
                                              <Edit2 className="w-4 h-4" />
                                            </button>
                                            <button
                                              onClick={() => removeVariant(variant)}
                                              className="text-red-600 hover:text-red-800 p-1"
                                              title="Excluir"
                                            >
                                              <Trash2 className="w-4 h-4" />
                                            </button>
                                          </div>
                                        )}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200 flex items-center justify-between sticky top-0 bg-white z-10">
              <h2 className="text-xl font-semibold">
                {editingProduct ? 'Editar Produto' : 'Novo Produto'}
              </h2>
              <button onClick={resetForm} className="text-gray-500 hover:text-gray-700">
                <X className="w-6 h-6" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-6">
              {/* Dados Básicos */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">Dados do Produto</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Nome do Produto *
                    </label>
                    <input
                      type="text"
                      value={formData.nome}
                      onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      placeholder="Ex: Puxador Barcelona"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      SKU Principal
                    </label>
                    <input
                      type="text"
                      value={formData.sku}
                      onChange={(e) => setFormData({ ...formData, sku: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      placeholder="Ex: PUX-BCN-001"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Código Interno
                    </label>
                    <input
                      type="text"
                      value={formData.codigo_interno}
                      onChange={(e) => setFormData({ ...formData, codigo_interno: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      placeholder="Código interno"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Unidade
                    </label>
                    <select
                      value={formData.unidade_id}
                      onChange={(e) => setFormData({ ...formData, unidade_id: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Selecione...</option>
                      {units.map((unit) => (
                        <option key={unit.id} value={unit.id}>{unit.sigla} - {unit.nome}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Categoria
                    </label>
                    <select
                      value={formData.categoria_id}
                      onChange={(e) => setFormData({ ...formData, categoria_id: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Selecione...</option>
                      {categories.map((cat) => (
                        <option key={cat.id} value={cat.id}>{cat.nome}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Marca
                    </label>
                    <select
                      value={formData.marca_id}
                      onChange={(e) => setFormData({ ...formData, marca_id: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Selecione...</option>
                      {brands.map((brand) => (
                        <option key={brand.id} value={brand.id}>{brand.nome}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Descrição
                  </label>
                  <textarea
                    value={formData.descricao}
                    onChange={(e) => setFormData({ ...formData, descricao: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    rows={3}
                    placeholder="Descrição do produto..."
                  />
                </div>
              </div>

              {/* Imagem */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">Imagem do Produto</h3>
                <div className="flex items-center gap-4">
                  <div className="flex-shrink-0">
                    {imagemPreview ? (
                      <div className="relative">
                        <img
                          src={imagemPreview}
                          alt="Preview"
                          className="w-24 h-24 object-cover rounded-lg border border-gray-300"
                        />
                        <button
                          type="button"
                          onClick={removeImage}
                          className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    ) : (
                      <div className="w-24 h-24 flex items-center justify-center bg-gray-100 rounded-lg border border-gray-300">
                        <Image className="w-8 h-8 text-gray-400" />
                      </div>
                    )}
                  </div>
                  <div>
                    <input
                      type="file"
                      ref={fileInputRef}
                      accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                      onChange={handleImageUpload}
                      className="hidden"
                      id="imagem-upload"
                    />
                    <label
                      htmlFor="imagem-upload"
                      className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg border border-gray-300 cursor-pointer hover:bg-gray-200"
                    >
                      <Upload className="w-4 h-4" />
                      {imagemPreview ? 'Trocar imagem' : 'Enviar imagem'}
                    </label>
                    <p className="text-xs text-gray-500 mt-1">Máximo 5MB (JPEG, PNG, GIF, WebP)</p>
                  </div>
                </div>
              </div>

              {/* Variantes */}
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-medium text-gray-900">Variações do Produto</h3>
                  <button
                    type="button"
                    onClick={addVariant}
                    className="flex items-center gap-2 px-3 py-1.5 bg-purple-600 text-white rounded-lg hover:bg-purple-700 text-sm"
                  >
                    <Plus className="w-4 h-4" />
                    Adicionar Variação
                  </button>
                </div>

                {variants.length === 0 ? (
                  <div className="bg-gray-50 rounded-lg p-8 text-center">
                    <Layers className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                    <p className="text-gray-500">Nenhuma variação adicionada.</p>
                    <p className="text-sm text-gray-400">Clique em "Adicionar Variação" para criar diferentes opções (cores, tamanhos, marcas)</p>
                  </div>
                ) : (
                  <div className="bg-white rounded border border-gray-200 overflow-hidden">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-100">
                        <tr>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">SKU</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Cor</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Tamanho</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Custo</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Venda</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Status</th>
                          <th className="px-4 py-2 text-right text-xs font-medium text-gray-500">Ações</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {variants.map((variant) => (
                          <tr key={variant.id} className="hover:bg-gray-50">
                            <td className="px-4 py-2 text-sm text-gray-900">{variant.sku_variacao || '-'}</td>
                            <td className="px-4 py-2 text-sm text-gray-500">{variant.cor || '-'}</td>
                            <td className="px-4 py-2 text-sm text-gray-500">{variant.tamanho_label || variant.medida_text || '-'}</td>
                            <td className="px-4 py-2 text-sm text-gray-500">
                              {variant.preco_custo ? `R$ ${parseFloat(variant.preco_custo).toFixed(2)}` : '-'}
                            </td>
                            <td className="px-4 py-2 text-sm text-gray-500">
                              {variant.preco_venda ? `R$ ${parseFloat(variant.preco_venda).toFixed(2)}` : '-'}
                            </td>
                            <td className="px-4 py-2">
                              <span className={`px-2 py-1 text-xs rounded-full ${
                                variant.ativo ? 'bg-green-100 text-green-800' : 'bg-gray-200 text-gray-600'
                              }`}>
                                {variant.ativo ? 'Ativo' : 'Inativo'}
                              </span>
                            </td>
                            <td className="px-4 py-2 text-right">
                              <div className="flex justify-end gap-1">
                                <button
                                  type="button"
                                  onClick={() => editVariant(variant)}
                                  className="text-blue-600 hover:text-blue-800 p-1"
                                  title="Editar"
                                >
                                  <Edit2 className="w-4 h-4" />
                                </button>
                                <button
                                  type="button"
                                  onClick={() => removeVariant(variant)}
                                  className="text-red-600 hover:text-red-800 p-1"
                                  title="Excluir"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {/* Ativo */}
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="ativo"
                  checked={formData.ativo}
                  onChange={(e) => setFormData({ ...formData, ativo: e.target.checked })}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <label htmlFor="ativo" className="ml-2 text-sm text-gray-700">
                  Produto ativo
                </label>
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t">
                <button
                  type="button"
                  onClick={resetForm}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  {editingProduct ? 'Atualizar Produto' : 'Criar Produto'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal de Variante */}
      {showVariantForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="p-6 border-b border-gray-200 flex items-center justify-between">
              <h2 className="text-xl font-semibold">
                {editingVariant ? 'Editar Variação' : 'Nova Variação'}
              </h2>
              <button onClick={() => setShowVariantForm(false)} className="text-gray-500 hover:text-gray-700">
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  SKU da Variação
                </label>
                <input
                  type="text"
                  value={variantForm.sku_variacao}
                  onChange={(e) => setVariantForm({ ...variantForm, sku_variacao: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="Ex: PUX-BCN-AZUL-P"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Cor
                </label>
                <select
                  value={variantForm.cor}
                  onChange={(e) => setVariantForm({ ...variantForm, cor: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Selecione...</option>
                  {availableOptions.cores.map(cor => (
                    <option key={cor} value={cor}>{cor}</option>
                  ))}
                  {variantForm.cor && !availableOptions.cores.includes(variantForm.cor) && (
                    <option value={variantForm.cor}>{variantForm.cor} (outro)</option>
                  )}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Medida (texto)
                </label>
                <input
                  type="text"
                  value={variantForm.medida_text}
                  onChange={(e) => setVariantForm({ ...variantForm, medida_text: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="Ex: 100x50cm"
                />
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Espessura (mm)
                  </label>
                  <input
                    type="number"
                    step="0.1"
                    min="0"
                    value={variantForm.espessura_mm}
                    onChange={(e) => setVariantForm({ ...variantForm, espessura_mm: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="Ex: 15"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Comprimento (cm)
                  </label>
                  <input
                    type="number"
                    step="0.1"
                    min="0"
                    value={variantForm.comprimento_cm}
                    onChange={(e) => setVariantForm({ ...variantForm, comprimento_cm: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="Ex: 100"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Largura (cm)
                  </label>
                  <input
                    type="number"
                    step="0.1"
                    min="0"
                    value={variantForm.largura_cm}
                    onChange={(e) => setVariantForm({ ...variantForm, largura_cm: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="Ex: 50"
                  />
                </div>
              </div>

              {variantForm.comprimento_cm > 0 && variantForm.largura_cm > 0 && variantForm.preco_venda > 0 && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-sm font-medium text-green-800">Preço por m²</div>
                      <div className="text-xs text-green-600">
                        {variantForm.comprimento_cm}cm x {variantForm.largura_cm}cm = {((variantForm.comprimento_cm * variantForm.largura_cm) / 10000).toFixed(4)}m²
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-lg font-bold text-green-800">
                        R$ {((variantForm.preco_venda / ((variantForm.comprimento_cm * variantForm.largura_cm) / 10000))).toFixed(2)}/m²
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {(availableOptions.cores.length === 0 || availableOptions.tamanhos.length === 0) && (
                <div className="text-xs text-amber-600 bg-amber-50 p-2 rounded">
                  Nenhuma opção disponível. Cadastre novas opções em: Produtos &gt; Opções de Produtos
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Preço de Custo (R$)
                  </label>
                  <div className="relative">
                    <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={variantForm.preco_custo}
                      onChange={(e) => setVariantForm({ ...variantForm, preco_custo: e.target.value })}
                      className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      placeholder="0,00"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Preço de Venda (R$)
                  </label>
                  <div className="relative">
                    <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={variantForm.preco_venda}
                      onChange={(e) => setVariantForm({ ...variantForm, preco_venda: e.target.value })}
                      className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      placeholder="0,00"
                    />
                  </div>
                </div>
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="variant_ativo"
                  checked={variantForm.ativo}
                  onChange={(e) => setVariantForm({ ...variantForm, ativo: e.target.checked })}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <label htmlFor="variant_ativo" className="ml-2 text-sm text-gray-700">
                  Variação ativa
                </label>
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowVariantForm(false)}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={saveVariant}
                  className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
                >
                  {editingVariant ? 'Atualizar' : 'Adicionar'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <ConfirmModal
        show={confirmModal.show}
        title={confirmModal.title}
        message={confirmModal.message}
        type={confirmModal.type}
        onConfirm={confirmModal.onConfirm}
        onCancel={() => setConfirmModal(prev => ({ ...prev, show: false }))}
      />
    </div>
  );
};

export default ProductsManager;
